import {Component, inject, input, output, SimpleChanges, OnInit, ViewChild} from '@angular/core';
import {CustomMilestone} from "../../models/custom-milestone.model";
import {FormValidity} from "../detail-view-wrapper.component";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {
  RDS_DATE_FORMATS,
  RdsButtonModule,
  RdsDatepickerModule,
  RdsDividerModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsNativeDateModule,
  RdsProgressSpinnerComponent,
  RdsBadgeModule,
  RdsSwitchModule,

} from "@rds/angular-components";
import {ValidationErrorCollector} from "../../models/validation-error-collector.model";
import {DataObject} from "../../models/metadata";
import {DateFormatter} from "../../models/date-formatter.model";
import {ExpandableInputComponent} from "../expandable-input/expandable-input.component";
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { StrategicPillarSelectorComponent } from '../../shared/strategic-pillar-selector/strategic-pillar-selector.component';
import { StrategicPillar, StrategicPillarService, EntityType } from '../../services/strategic-pillar.service';
import { AudienceSelectorComponent } from '../../shared/audience-selector/audience-selector.component';
export type VisibilityType = 'visible' | 'hidden'

type DetailedViewForm = FormGroup<{
  title: FormControl<string | null>;
  date: FormControl<Date  | null>;
  keyWords: FormArray<FormControl<string | null>>;
  link: FormControl<string | null>;
  notes: FormControl<string | null>;
  isVisible: FormControl<boolean | null>;
}>

@Component({
  selector: 'app-custom-milestone-detail-view',
  standalone: true,
  imports: [
    FormsModule,
    RdsButtonModule,
    RdsDividerModule,
    RdsFormFieldModule,
    RdsIconComponent,
    RdsProgressSpinnerComponent,
    ReactiveFormsModule,
    RdsDatepickerModule,
    RdsNativeDateModule,
    RdsBadgeModule,
    RdsSwitchModule,
    ExpandableInputComponent,
  StrategicPillarSelectorComponent,
  AudienceSelectorComponent,
  ],
  providers: [
    { provide: RDS_DATE_FORMATS, useValue: DateFormatter.getDatePickerFormat() },
  ],
  templateUrl: './custom-milestone-detail-view.component.html',
  styleUrls: ['./custom-milestone-detail-view.component.scss']
})
export class CustomMilestoneDetailViewComponent implements OnInit {
  customMilestone = input.required<CustomMilestone>();
  isLoadingChanges = input.required<boolean>()
  newCustomMilestone = output<{ data: CustomMilestone, valid: FormValidity }>()
  deleteCustomMilestone = output<DataObject>()
  formBuilder = inject(FormBuilder)
  form!: DetailedViewForm
  editorActionsVisible: VisibilityType = 'hidden'
  isViewer: boolean = true

  @ViewChild(StrategicPillarSelectorComponent) strategicPillarSelector!: StrategicPillarSelectorComponent;
  private strategicPillarService = inject(StrategicPillarService);
  pendingStrategicPillars: { pillarIds: number[], pillars: StrategicPillar[] } | null = null;

  constructor(private userService: UserService) {}

  ngOnInit() {
    this.userService.getUserData().then((user: User) => {
      this.editorActionsVisible = user.isViewer ? 'hidden' : 'visible'
      this.isViewer = user.isViewer
      this.setupForm()
    }).catch((error: any) => {
      console.error('Error fetching user data', error);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['customMilestone']){
      this.setupForm()
    }
  }

  private setupForm(){
    const m = this.customMilestone()
    this.form = this.formBuilder.group({
      title: [{ value: m.title, disabled: this.isViewer }, Validators.required],
      date: [{ value: m.date, disabled: this.isViewer }, Validators.required],
      keyWords: this.formBuilder.array(m.keyWords.map(k => this.formBuilder.control({ value: k, disabled: this.isViewer }, this.keywordValidator))),
      link: [{ value: m.link, disabled: this.isViewer }],
      notes: [{ value: m.notes, disabled: this.isViewer }],
      isVisible: this.formBuilder.control({         
        value: m.isVisible == false ? m.isVisible : true, 
        disabled: this.isViewer})
    })
  }

  keywordValidator(keywordControl: AbstractControl<string>) {
    const keyword = keywordControl.value
    if (!keyword || typeof keyword !== 'string' || keyword.trim() == "")
      return {emptyKeywordError: true} as ValidationErrors
    else
      return null
  }

  addKeyword() {
    this.form.controls.keyWords.push(this.formBuilder.control("", this.keywordValidator))
  }

  removeKeyword(i: number){
    this.form.controls.keyWords.removeAt(i)
  }

  clearEmptyKeywords(){
    const indices: [string | null, number][] = this.form.controls.keyWords.value.map((x, i) => [x, i] as [string | null, number]).filter(k => k[0] === "")
    for(let [offset, idx] of indices.entries()){
      this.form.controls.keyWords.removeAt(idx[1] - offset)
    }
  }

  onKeywordKeypress(e: KeyboardEvent){
    if(e.code == "Enter"){
      e.preventDefault()
    }
  }

  onDelete(){
    this.deleteCustomMilestone.emit(this.customMilestone() as DataObject)
  }

  onSubmit() {
    const errors = new ValidationErrorCollector(this.form).errors;
    if (errors["emptyKeywordError"]) {
      this.clearEmptyKeywords();
    }
    const validity: FormValidity = this.form.valid ? 'valid' : 'required';
    const copy = Object.assign(new CustomMilestone(), JSON.parse(JSON.stringify(this.customMilestone())));
    const post = Object.assign(copy, this.form.value) as CustomMilestone;

    // Just emit the form data - strategic pillar update will happen after confirmation
    this.newCustomMilestone.emit({ data: post, valid: validity });
  }

  // New method to handle strategic pillar update after confirmation
  async updateStrategicPillarAfterConfirmation(): Promise<CustomMilestone | null> {
    const abstractID = this.customMilestone().abstractID;
    if (this.pendingStrategicPillars && abstractID) {
      try {
        const response = await this.strategicPillarService.updateMultipleStrategicPillarAssignments(
          'custom_milestone',
          abstractID,
          this.pendingStrategicPillars.pillarIds
        ).toPromise();
        if (response && response.success) {
          // Fetch and update the custom milestone's strategicPillars property
          const pillars = await this.strategicPillarService.getTaggedPillars('custom_milestone', abstractID).toPromise();
          if (pillars) {
            this.customMilestone().strategicPillars = pillars;
          }
          this.strategicPillarSelector.markAsSaved();
          this.pendingStrategicPillars = null;
          return this.customMilestone(); // Return updated custom milestone
        }
      } catch (error) {
        console.error('Error updating strategic pillar assignment:', error);
      }
    }
    return null;
  }

  openLink(url: string = this.form.controls.link.value || '') { 
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    const newWindow = window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  // Strategic Pillar event handlers
  onPillarChanged(selection: { pillarIds: number[], pillars: StrategicPillar[] }) {
    // Store the pending change for when user clicks Save
    this.pendingStrategicPillars = selection;
  }
}
